import { MILLISECONDS_IN_MINUTE } from '@stdlib/constants-time';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import type { SearchBookingsResponse } from 'app/typings/core/admin/bookings/bookingList';
import type { BookingStatus } from 'app/typings/core/admin/bookings/enum/bookingStatus';
import { BOOKING_STATUS } from 'app/typings/core/admin/bookings/enum/bookingStatus';
import type { SupportedPsp } from 'app/typings/core/common/enum/supportedPsp';
import type { SupportedPspMode } from 'app/typings/core/common/enum/supportedPspMode';
import { apiRequest } from 'app/utils/request/api';

import { CORE_QUERY_KEYS, ENDPOINTS } from '.';

export const BOOKINGS_SORTING_COLUMN = [
  'CREATED_AT',
  'FIRST_NAME',
  'LAST_NAME',
  'CHECKIN',
] as const;
export type BookingsSortingColumn = (typeof BOOKINGS_SORTING_COLUMN)[number];

export const BOOKING_STATUS_API = [
  'ACCEPTED',
  'CANCELED',
  'PENDING',
  'ABORTED',
] as const;
export type BookingStatusAPI = (typeof BOOKING_STATUS_API)[number];

export const BOOKING_STATUS_TO_API: Partial<
  Record<BookingStatus, BookingStatusAPI>
> = {
  [BOOKING_STATUS.ACCEPTED]: 'ACCEPTED',
  [BOOKING_STATUS.CANCELED]: 'CANCELED',
  [BOOKING_STATUS.PENDING]: 'PENDING',
  [BOOKING_STATUS.ABORTED]: 'ABORTED',
};

export type BookingsQueryParams = {
  search?: string;
  sortingOrder?: 'ASC' | 'DESC';
  sortingColumn?: BookingsSortingColumn;
  status?: BookingStatusAPI[];
  beginning?: string;
  ending?: string;
  pageNumber?: number;
  pspFilter?: SupportedPsp;
  pspModeFilter?: SupportedPspMode;
};

export function useBookings(queryParams: BookingsQueryParams = {}) {
  const key = useMemo(
    () => [CORE_QUERY_KEYS.bookings, queryParams],
    [queryParams]
  );

  return useQuery({
    queryKey: key,
    queryFn: () =>
      apiRequest({
        path: ENDPOINTS.getBookings,
        queryParams,
      }) as Promise<SearchBookingsResponse>,
    staleTime: 5 * MILLISECONDS_IN_MINUTE,
  });
}
